<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('settings.settingsLabel') }}</span>
        <h3 class="page-title">{{ $t('settings.language.editLanguage') }}</h3>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push('/ustawienia/jezyki')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          {{ $t('ui.backToList') }}
        </d-button>
        <d-button theme="success" @click.prevent="validateForm">{{ $t('ui.saveChanges') }}y</d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form>
      <d-card class="mb-5">
        <d-tabs v-sticky-tabs card>
          <!-- Dane ogólne -->
          <d-tab
            :title="$t('ui.generalInfo')"
            active>
            <h4 class="mb-3 pb-3 border-bottom">
              {{ $t('ui.generalInfo') }}
            </h4>

            <validation-observer ref="language-edit-form" tag="form" v-if="loaded">
              <div class="form-group">
                <label for="name">
                  {{ $t('settings.language.languageName') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="name"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="name" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="name">
                  {{ $t('settings.language.languageCode') }}:
                </label>

                <validation-provider
                  rules="required|only-letters-and-dashes|min:2|max:5"
                  name="code"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="code" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </validation-observer>

            <div v-if="!loaded" class="pb-3 pt-3 text-center">
              {{ $t('ui.dataLoading') }}&hellip;
            </div>

            <d-alert
              v-if="loadError"
              show
              theme="warning">
              {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
            </d-alert>
          </d-tab>

          <d-tab :title="$t('settings.language.translations')">
            <h4 class="pb-3 mb-0 border-bottom d-flex">
              {{ $t('settings.transaltion.translations') }}

              <d-button
                class="ml-auto"
                theme="success"
                @click.prevent="addTranslation">
                <i class="material-icons">add</i>
                {{ $t('settings.transaltion.addTranslation') }}
              </d-button>
            </h4>

            <table class="table mt-2 mb-0 full-width">
              <thead class="bg-light">
                <tr>
                  <th scope="col" class="border-0" style="width: 150px;">{{ $t('ui.language') }}</th>
                  <th scope="col" class="border-0" style="width: calc(100% - 350px);">{{ $t('settings.language.languageName') }}</th>
                  <th scope="col" class="border-0" style="width: 200px;">{{ $t('ui.actions') }}</th>
                </tr>
              </thead>
              <tbody v-if="language_translations.length">
                <tr
                  v-for="(translation, index) of language_translations"
                  :key="'price-' + index">
                  <td class="align-middle">
                    {{ getLanguageByID(translation.language_id) }}
                  </td>
                  <td class="align-middle">
                    {{ translation.name }}
                  </td>
                  <td class="align-middle">
                    <d-button
                      theme="primary"
                      @click.prevent="editTranslation(translation)"
                      class="mr-2"
                      :title="$t('ui.edit')">
                      <i class="material-icons">edit</i>
                    </d-button>
                    <d-button
                      theme="danger"
                      :disabled="removeTranslationInProgress"
                      @click.prevent="removeTranslation(translation.id)"
                      :title="$t('ui.delete')">
                      <i class="material-icons">delete</i>
                    </d-button>
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td colspan="3" class="text-center">
                    {{ $t('settings.language.noTranslationsForThisLanguage') }}&hellip;
                  </td>
                </tr>
              </tbody>
            </table>
          </d-tab>
        </d-tabs>
      </d-card>
    </d-form>

    <language-translation-new-popup
      v-if="showAddTranslationPopup"
      :languageID="this.id"
      :languages="languages" />

    <language-translation-edit-popup
      v-if="showEditTranslationPopup"
      :languageID="this.id"
      :translationData="selectedTranslation"
      :languages="languages" />
  </d-container>
</template>

<script>
import FormUtils from './../../utils/FormUtils.js';
import ListUtils from './../../utils/ListUtils.js';
import LanguageTranslationNewPopup from './LanguageTranslationNewPopup';
import LanguageTranslationEditPopup from './LanguageTranslationEditPopup';

export default {
  name: 'language-edit',
  components: {
    'language-translation-new-popup': LanguageTranslationNewPopup,
    'language-translation-edit-popup': LanguageTranslationEditPopup
  },
  data() {
    return {
      id: 0,
      name: '',
      code: '',
      loaded: false,
      loadError: false,
      languages: [],
      loadedLanguages: false,
      language_translations: [],
      showAddTranslationPopup: false,
      showEditTranslationPopup: false,
      selectedTranslation: 0,
      removeTranslationInProgress: false
    };
  },
  mounted () {
    FormUtils.loadItemData(this, {
      method: 'get',
      endpointBase: '/languages/item/',
      instanceFields: ['name', 'code', 'language_translations'],
      loadedKey: 'loaded',
      params: {
        with: ['languageTranslations']
      }
    });

    this.loadLanguages();
    this.$bus.$on('language-translation-new-popup-close', this.closeAddTranslationPopup);
    this.$bus.$on('language-translation-edit-popup-close', this.closeEditTranslationPopup);
    this.$bus.$on('language-reload-translations', this.loadTranslations);
  },
  methods: {
    validateForm () {
      FormUtils.validate(this.$refs['language-edit-form'], this.submitForm);
    },
    submitForm () {
      FormUtils.submit({
        $http: this.$http,
        endpoint: '/languages/update/' + this.id,
        formData: {
          name: this.name,
          code: this.code
        },
        successTitle: this.$t('settings.language.editLanguageSuccessTitle'),
        successText: this.$t('settings.language.editLanguageSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('settings.language.editLanguageErrorText'),
        successAction: () => {
          this.$router.push('/ustawienia/jezyki')
        }
      });
    },
    loadLanguages () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/languages/items',
        loadedKey: 'loadedLanguages',
        outputKey: 'languages',
        errorKey: 'loadError'
      });
    },
    getLanguageByID (languageID) {
      let filteredLanguages = this.languages.filter(language => language.id === languageID);

      if (filteredLanguages.length) {
        return filteredLanguages[0].name;
      }

      return '';
    },
    loadTranslations () {
      FormUtils.loadItemData(this, {
        method: 'get',
        endpointBase: '/languages/item/',
        instanceFields: ['language_translations'],
        loadedKey: null,
        params: {
          with: ['languageTranslations']
        }
      });
    },
    addTranslation () {
      this.showAddTranslationPopup = true;
    },
    editTranslation (translationData) {
      this.selectedTranslation = JSON.parse(JSON.stringify(translationData));
      this.showEditTranslationPopup = true;
    },
    closeAddTranslationPopup () {
      this.showAddTranslationPopup = false;
    },
    closeEditTranslationPopup () {
      this.showEditTranslationPopup = false;
    },
    removeTranslation (translationID) {
      this.removeTranslationInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/languages/translations/delete/',
        id: translationID,
        successTitle: this.$t('settings.transaltion.deleteTranslationSuccessTitle'),
        successText: this.$t('settings.transaltion.deleteTranslationSuccessText'),
        confirmTitle: this.$t('settings.transaltion.deleteTranslationConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('settings.transaltion.deleteTranslationErrorText'),
        successAction: () => {
          this.removeTranslationInProgress = false;
          this.loadTranslations();
        },
        cancelAction: () => {
          this.removeTranslationInProgress = false;
        }
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('language-translation-new-popup-close', this.closeAddTranslationPopup);
    this.$bus.$off('language-translation-edit-popup-close', this.closeEditTranslationPopup);
    this.$bus.$off('language-reload-translations', this.loadTranslations);
  }
};
</script>
